
import React, { useState, useRef, useEffect, ReactElement, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface PopoverProps {
    children: ReactElement;
    content: ReactElement<{ close: () => void }>;
    wrapperClassName?: string;
}

const Popover: React.FC<PopoverProps> = ({ children, content, wrapperClassName = 'inline-block' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const [style, setStyle] = useState<React.CSSProperties>({
        position: 'fixed',
        opacity: 0,
        pointerEvents: 'none',
        zIndex: 50,
    });

    const togglePopover = useCallback((e: React.MouseEvent) => {
        // e.stopPropagation(); // This is not needed and can block parent events.
        setIsOpen(prev => !prev);
    }, []);

    const closePopover = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        if (isOpen && triggerRef.current && popoverRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            // The popover is rendered with opacity 0, so we can measure it.
            const popoverRect = popoverRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const margin = 8; // 8px margin from viewport edges and trigger

            const newStyle: React.CSSProperties = {
                position: 'fixed',
                opacity: 1,
                pointerEvents: 'auto',
                zIndex: 50,
            };

            // Vertical positioning: check space below and above
            const spaceBelow = viewportHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;

            if (spaceBelow < popoverRect.height + margin && spaceAbove > spaceBelow) {
                // Not enough space below, and more space above, so position ABOVE
                newStyle.top = `${triggerRect.top - popoverRect.height - margin}px`;
            } else {
                // Default, position BELOW
                newStyle.top = `${triggerRect.bottom + margin}px`;
            }

            // Horizontal positioning
            let left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);

            // Clamp to viewport to prevent going off-screen
            if (left < margin) {
                left = margin;
            }
            if (left + popoverRect.width > viewportWidth - margin) {
                left = viewportWidth - popoverRect.width - margin;
            }
            newStyle.left = `${left}px`;

            setStyle(newStyle);
        } else {
            // When closing, reset to initial hidden state
            setStyle({
                position: 'fixed',
                opacity: 0,
                pointerEvents: 'none',
                zIndex: 50,
            });
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleGlobalClick = (event: MouseEvent) => {
             if (
                triggerRef.current && triggerRef.current.contains(event.target as Node) ||
                popoverRef.current && popoverRef.current.contains(event.target as Node)
            ) {
                return;
            }
            closePopover();
        };
        
        // Use setTimeout to allow the current click event to finish before attaching the new one
        setTimeout(() => {
            document.addEventListener('mousedown', handleGlobalClick);
        }, 0);
        
        return () => document.removeEventListener('mousedown', handleGlobalClick);
    }, [isOpen, closePopover]);

    const contentWithClose = React.cloneElement(content, { close: closePopover });

    const popoverContent = (
        <div
            ref={popoverRef}
            style={style}
            className={`bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 transition-opacity duration-150`}
        >
            <div className="py-1 rounded-md bg-white dark:bg-slate-800 shadow-xs">
                {contentWithClose}
            </div>
        </div>
    );
    
    // Create a portal if the document object is available (i.e., we are in the browser)
    const portal = typeof document !== 'undefined' ? ReactDOM.createPortal(popoverContent, document.body) : null;

    return (
        <div ref={triggerRef} className={`${wrapperClassName}`}>
            <div onClick={togglePopover} className="h-full w-full">
                {children}
            </div>
            {isOpen && portal}
        </div>
    );
};

export default Popover;
